import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import dateFormat from "dateformat";
import { format } from "date-fns";
import ordinal from "ordinal";

import { FileService } from "src/utils/file.service";

import { CourseContext } from "../../../context";

import { AssessmentCard, LessonCard, PodcastCard, SectionCard, SimulationCard } from "../../cards";
import Thumbnail from "src/components/thumbnail";
import { flattenContents, getIndexForContent } from "../../../helper";
import { CourseLearnerAttempt, LearningRecordCourse, SimulationSession } from "src/types/coaching/courses";
import { fetchAttemptsForCourse, fetchCourseLearnersRecord, fetchDraftCourse, fetchLearnersAssessmentReport } from "src/redux/actions/coaching/courses";

import _ from "lodash";
import cx from "classnames";
import styles from "./detail.module.scss";
import Tooltip from "src/components/tooltip";

interface EditCourseReportsDetailProps {
  learner: any;
  onOpenAssessment: (attempt: any, session?: any) => void;
  showReport: boolean;
  handleCloseReport: () => void;
  initialAttempt?: CourseLearnerAttempt;
}

export const EditCourseReportsDetail: React.FC<EditCourseReportsDetailProps> = ({ learner, onOpenAssessment, showReport=false, handleCloseReport, initialAttempt}) => {
  const dispatch = useDispatch();
  const { id: courseId, learningId } = useParams();

  const learners = useSelector((state: any) =>
    _.get(state.courses, "attemptLearners.items", []),
  );

  const course: LearningRecordCourse = useSelector((state: any) =>
    _.get(state.courses, `learnerReport.${learningId}.item`)
  );

  const scores = useMemo(() => {
    return (
      learners.slice(0, 3).map((a: any) => Math.round((a.score ?? 0) * 100)) || []
    );
  }, [learners]);

  const attempts = learners.map((item, index, arr) => {
    return {
      ...item,
      attemptNumber: arr.length - index,
    };
  });

  const currentAttempt = attempts.find((a) => a.id === learner.id);

  const bestScore = useMemo(() => {
    const best = _.maxBy(attempts, "score") || learner;
    return best ? Math.round(best.score * 100) : "NA";
  }, [attempts]);

  const learnerBestScore = useSelector((state: any) => {
    return _.get(state, `courses.course.${courseId}.item.bestScore`);
  });

  const totalDuration = useMemo(() => {
    return _.sumBy(attempts, "duration");
  }, [attempts]);

  useEffect(() => {
    if (courseId && learner?.user?.id) {
      dispatch(
        fetchAttemptsForCourse({
          id: courseId,
          learnerId: learner.user.id,
        }),
      );
    }
  }, [courseId, learner?.user?.id]);

  useEffect(() => {
    if (courseId) {
      dispatch(fetchDraftCourse(courseId));
    }
  }, [courseId]);

  useEffect(() => {
    if (learningId) {
      dispatch(fetchCourseLearnersRecord(learningId));
    }
  }, [learningId]);

  return (
    <div className={styles.detailContainer}>
      <div className={styles.profileContainer}>
        {showReport && (
          <Tooltip content="Toggle sidebar" placement="right">
            <button className={styles.toggleButton} onClick={handleCloseReport}>
              <i className={`paperflite-side-panel`}></i>
            </button>
          </Tooltip>
        )}
        <Thumbnail
          media={learner.user?.icon}
          defaultImage="/images/assets/default_asset.jpg"
          className={styles.profileAvatar}
        />

        <div className={styles.details}>
          <div className={styles.profileInfo}>
            <span className={styles.name}>
              {learner.user.firstName || ""} {learner.user.lastName}
            </span>
            <span className={styles.email}>{learner.user.email}</span>
          </div>

          {learner?.rank > 0 && (
            <div className={styles.rank}>
              <span className={styles.symbol}>#</span>
              <span className={styles.number}>{learner.rank}</span>
              <div className={styles.textContainer}>
                <span className={styles.name}>best score</span>
                <span className={styles.score}>{bestScore}%</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={styles.infoContainer}>
        <Metrics
          name="latest score"
          value={
            learner?.score != null && course?.sessions?.some(s => (s.type === 'assessment' || s.type === 'simulation'))
              ? `${Math.round(learner.score * 100)}%`
              : "NA"
          }
          subTitle={`average best score across the team is ${learnerBestScore ? Math.round(learnerBestScore * 100) : "na"}%`}
        />

        <Metrics
          name="attempts taken"
          value={learner.completedAttempts ? `#${learner.completedAttempts}` : "NA"}
          attemptsScores={scores}
        />

        <Metrics
          name="ramp-up time"
          value={FileService.getTimeToComplete(
            initialAttempt?.createdDate,
            initialAttempt?.modifiedDate,
          )}
          subTitle={
            initialAttempt?.assignedBy
              ? `assigned on ${dateFormat(initialAttempt?.createdDate, "dd mmm yyyy")}`
              : `assigned on ${dateFormat(initialAttempt?.createdDate, "dd mmm yyyy")}`
          }
        />

        <Metrics
          name="time spent"
          value={FileService.formatDuration(totalDuration, "short")}
          subTitle={`across ${attempts.length} completed attempt${attempts.length === 1 ? "" : "s"}`}
        />
      </div>

      <div className={styles.detailedReport}>
        <LearnerDetailsReport learner={currentAttempt} attempts={attempts} course={course} onOpenAssessment={onOpenAssessment} />
      </div>
    </div>
  );
};

interface MetricsProps {
  name: string;
  value: string;
  subTitle?: string;
  attemptsScores?: number[];
}

const Metrics: React.FC<MetricsProps> = ({
  name,
  value,
  subTitle,
  attemptsScores,
}) => {
  return (
    <div className={styles.infoCard}>
      <div className={styles.metrics}>
        <span className={styles.name}>{name}</span>
        <span className={styles.value}>{value}</span>

        {_.size(attemptsScores) > 1 && (
          <div className={styles.progressContainer}>
            {attemptsScores?.map((score: number, index: number) => {
              const isLatest = index === 0;
              return (
                <div key={index} className={styles.attemptProgress}>
                  <div
                    className={cx(styles.fill, {
                      [styles.lightFill]: !isLatest,
                    })}
                    style={{ width: `${score === 0 ? 1 : score}%` }}
                  />
                  <span className={styles.label}>{score}%</span>
                </div>
              );
            })}
          </div>
        )}

        {subTitle && <span className={styles.subTitle}>{subTitle}</span>}
      </div>
    </div>
  );
};

interface LearnerDetailsReportProps {
  learner: any;
  attempts: any[];
  course: any;
  onOpenAssessment: (attempt: any, session?: any) => void;
  selectedAttemptId?: string;
}

const LearnerDetailsReport: React.FC<LearnerDetailsReportProps> = ({
  learner,
  attempts,
  course,
  onOpenAssessment,
}) => {
  const dispatch = useDispatch();

  const prevLearnerIdRef = React.useRef<string | undefined>();

  const courseContext = useContext(CourseContext);

  const [searchParams] = useSearchParams();
  const { learningId } = useParams();

  const [selectedAttempt, setSelectedAttempt] = useState(learner);

  const learnersCourse = useSelector((state: any) =>
    _.get(state, "courses.learnerReport")
  );
  const learnerItem = learnersCourse?.[learningId!]?.item;

  const sessions = useSelector((state: any) =>
    _.get(state, `courses.learnerReport.${selectedAttempt?.id}.item.sessions`, [])
  );

  const allContents = useMemo(() => {
    return flattenContents(course?.course?.contents || []);
  }, [course]);

  const learnersCourseLoading = useSelector((state: any) => {
    return _.get(state.courses, `learnerReport[${selectedAttempt?.id}].loading`);
  });

  const currentAttempt = useMemo(() => {
    return attempts.find(a => a.id === selectedAttempt?.id) || selectedAttempt;
  }, [attempts, selectedAttempt]);

  const getAssessmentForContent = (content, flatContents) => {
    if (content.entityType === 'assessment') return content;

    if (content.entityType === 'section') {
      return flatContents.find(f => f.parent === content.uuid && f.entityType === 'assessment');
    }

    return null;
  };

  const handleOpenCallRecording = (session: SimulationSession) => {
    if (!currentAttempt || !session) return;
  
    courseContext?.openCallRecording?.({
      attempt: currentAttempt,
      session,
    });
  };

  const handleViewReport = (content) => {
    const assessmentItem = getAssessmentForContent(content, allContents);
    if (!learnerItem) {
      console.error("Learner item NOT FOUND for learningId:", learningId);
      return;
    }

    const courseId = learnerItem?.course?.id;
    const learningRecordId = currentAttempt?.id;
    const matchedSession = sessions.find(s => s.type === 'assessment' && s.id === assessmentItem.id);

    dispatch(
      fetchLearnersAssessmentReport({
        course: courseId,
        learnerRecordId: learningRecordId,
        id: assessmentItem.id,
        type: assessmentItem.entityType,
        session: matchedSession?.session
      })
    );
    onOpenAssessment(currentAttempt, matchedSession);
  };

  const onSelect = (attempt: any) => {
    setSelectedAttempt(attempt);
    const selectedLearningRecordId = attempt.id;
    dispatch(fetchCourseLearnersRecord(selectedLearningRecordId));
  };

  useEffect(() => {
    if (!selectedAttempt || prevLearnerIdRef.current !== learner?.id) {
      const initial = attempts.find(a => a.id === learner?.id) || attempts[0];
      setSelectedAttempt(initial);
      prevLearnerIdRef.current = learner?.id;
    }
  }, [learner, attempts]);

  const totalDuration = currentAttempt?.duration;

  return (
    <div className={styles.learnerPerformance}>
      <div className={styles.header}>
        <div className={styles.titleContainer}>
          <div className={styles.title}>breakdown</div>
          <div className={styles.details}>
            <div className={styles.metadata}>
              completed on {currentAttempt?.modifiedDate
                ? dateFormat(new Date(currentAttempt.modifiedDate), "dd mmm yyyy, HH:MM:ss")
                : "NA"}
            </div>
            <div className={styles.divider} />
            <div className={styles.metadata}>
              duration {FileService.formatTimeDuration(totalDuration)}
            </div>
          </div>
        </div>
        <div className={styles.actions}>


          {_.size(attempts) > 1 && (
            <div className={styles.attemtsFilterContainer}>
              <DropDown
                selected={selectedAttempt}
                attempts={attempts}
                onSelect={onSelect}
              />
            </div>
          )}
        </div>
      </div>
      {learnersCourseLoading && <ReportPlaceholder />}

      {!learnersCourseLoading && (
        <div className={styles.contentsList}>
          {course?.course?.contents?.map((content, index) => {
            const type = _.get(content, "entityType");
            const key = content.uuid || index;

            const contentIndex = getIndexForContent(
              course.course.contents || [],
              content.uuid,
              content.parent
            );

            const session = sessions.find(s => s.id === content.id);
            const duration = session?.duration || 0;
            const score = Math.round(session?.score * 2 * 10) || 0;

            const handleSimulationClick = (childContent: any) => {
              const session = sessions.find(s => s.id === childContent.id);
              courseContext?.openSimulationReport?.({
                attempt: currentAttempt,
                session,
              });
            };

            return (
              <React.Fragment key={key}>
                {type === "lesson" && (
                  <div className={styles.cardWrapper}>
                    <LessonCard
                      lessonIndex={contentIndex}
                      lesson={content}
                      index={index}
                      active={false}
                      mode='view'
                      readonly
                      onClick={() => { }}
                    >
                      {/* <div className={styles.sessionDuration}>
                        {FileService.formatDuration(duration, 'short')}
                      </div> */}
                    </LessonCard>
                  </div>
                )}
                {type === "assessment" && (
                  <div className={styles.cardWrapper}>
                    <AssessmentCard
                      assessment={content}
                      assessmentIndex={contentIndex}
                      index={index}
                      active={false}
                      mode="view"
                      readonly
                      onClick={() => handleViewReport(content)}
                    >
                      <div className={styles.metadata}>
                        <i className={cx(styles.arrowUp, "paperflite-arrow-up")} />
                      </div>
                    </AssessmentCard>
                  </div>
                )}

                {type === "section" && (
                  <div className={styles.cardWrapper}>
                    <SectionCard
                      className={styles.sectionCard}
                      section={content}
                      index={index}
                      sectionIndex={contentIndex}
                      active={false}
                      viewReport={true}
                      mode="view"
                      readonly
                      handleViewReport={handleViewReport}
                      onSimulationClick={handleSimulationClick}
                      onClick={() => { }}
                      renderChildrenForContent={(childContent) => {
                        if (childContent.entityType === 'lesson' || childContent.entityType === 'podcast') {
                          const session = sessions.find(s => s.id === childContent.id);
                          const duration = session?.duration || 0;
                          return (
                            // <div className={styles.sessionDuration}>
                            //   {FileService.formatDuration(duration, 'short')}
                            // </div>
                            null
                          );
                        }

                        if (childContent.entityType === 'assessment') {
                          return (
                            <div className={styles.metadata}>
                              <i className={cx(styles.arrowUp, "paperflite-arrow-up")} />
                            </div>
                          );
                        }

                        if (childContent.entityType === 'simulation') {
                          const session = sessions.find(s => s.id === childContent.id);
                          const score = Math.round(session?.score * 2 * 10) || 0;
                          return (
                           <div className={styles.wrapper}>
                            <div className={styles.sessionScore}>
                              {score === 0 ? '~' : `${score}%`}
                            </div>
                              <div className={styles.divider} />
                              {learner?.status === "COMPLETED" && (
                                <div className={styles.callRecording}>
                                  <button className={styles.action} onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenCallRecording(session);
                                  }}>
                                    <i className="paperflite-waveform" />
                                  </button>
                                </div>
                              )}
                              <div className={styles.divider} />
                              <i className="paperflite-document" onClick={(e) => {
                                e.stopPropagation();
                                courseContext?.openSimulationReport?.({
                                  attempt: currentAttempt,
                                  session,
                                });
                              }} />
                            </div>
                          );
                        }
                        return false;
                      }}
                    />
                  </div>
                )}

                {type === "podcast" && (
                  <div className={styles.cardWrapper}>
                    <PodcastCard
                      asset={content}
                      index={index}
                      lessonIndex={contentIndex}
                      active={false}
                      mode="view"
                      readonly
                      onClick={() => { }}
                    >
                      {/* <div className={styles.sessionDuration}>
                        {FileService.formatDuration(duration, 'short')}
                      </div> */}
                    </PodcastCard>
                  </div>
                )}

                <div
                  onClick={() => {
                    courseContext?.openSimulationReport?.({
                      attempt: currentAttempt,
                      session,
                    });
                  }}
                >
                {type === "simulation" && (
                  <div className={styles.cardWrapper}>
                    <SimulationCard
                      asset={content}
                      index={index}
                      lessonIndex={contentIndex}
                      active={false}
                      mode="view"
                      readonly
                      onClick={() => { }}
                    >
                      <div className={styles.wrapper}>
                      <div className={styles.sessionScore}>
                        {score === 0 ? '~' : `${score}%`}
                      </div>
                      <div className={styles.divider} />
                      {learner?.status === "COMPLETED" && (
                          <div className={styles.callRecording}>
                              <button className={styles.action} onClick={(e) => {
                                e.stopPropagation();
                                handleOpenCallRecording(session);
                              }}>
                              <i className="paperflite-waveform" />
                            </button>
                          </div>
                        )}
                         <div className={styles.divider} />
                          <i className="paperflite-document" onClick={() => {
                            courseContext?.openSimulationReport?.({
                              attempt: currentAttempt,
                              session,
                            });
                          }} />
                      </div>
                    </SimulationCard>
                  </div>
                )}
                </div>
                <div className={styles.dividerLine}></div>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
};

interface DropDownProps {
  attempts: any[];
  selected: any;
  onSelect?: (item: any) => void;
}

export const DropDown: React.FC<DropDownProps> = ({
  attempts,
  selected,
  onSelect,
}) => {
  const [show, setShow] = React.useState(false);

  const handleSelect = (item: any) => {
    setShow(false);
    if (onSelect) {
      onSelect(item);
    }
  };

  if (!selected) {
    return (
      <div className={cx(styles.selectDropdown, styles.placeholder)}></div>
    );
  }

  return (
    <div className={styles.selectDropdown}>
      <Dropdown>
        <span onClick={() => setShow(!show)}>
          <Dropdown.Toggle className={styles.button} as={"button"}>
            <div className={styles.atttempt}>
              <span className={styles.score}>
                {`${Math.round(selected.score * 100)} %` || "na"}
              </span>
              <div className={styles.divider} />
              <span className={styles.attemptCount}>
                {ordinal(selected?.attemptNumber) || "na"} <span className={styles.text}>attempt</span>
              </span>
            </div>
            <i
              className={cx("paperflite-dropdown", {
                [styles.openModal]: show,
              })}
            ></i>
          </Dropdown.Toggle>
        </span>

        <Dropdown.Menu
          className={styles.dropdown}
          popperConfig={{
            modifiers: [{ name: "offset", options: { offset: [0, 6] } }],
          }}
        >
          {attempts.map((attempt) => {
            const date = new Date(attempt?.modifiedDate).toISOString();
            const formattedDateTime = format(date, "do MMM hh:mm a");

            const active = attempt.id === selected.id;
            return (
              <Dropdown.Item
                key={attempt.attemptNumber}
                className={cx(styles.dropdownItem, { [styles.active]: active })}
                onClick={() => handleSelect(attempt)}
              >
                {String(attempt.attemptNumber).padStart(2, "0")} /{" "}
                {Math.round(attempt.score * 100)} % / {formattedDateTime}
                {active && <i className="paperflite-tick-selected"></i>}
              </Dropdown.Item>
            );
          })}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

const ReportPlaceholder: React.FC = () => {
  return (
    <div className={styles.container}>
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className={styles.card}>
          <div className={styles.thumbnail} />
          <div className={styles.content}>
            <div className={styles.titleLine} />
            <div className={styles.metaLine} />
          </div>
        </div>
      ))}
    </div>
  );
};
